import React, { useState } from "react";
import { X, Loader } from "lucide-react";
import { toast } from "react-toastify";
import useAadhaarUpload from "../hooks/useAadhaarUpload";
import AadhaarImageBox from "./AadhaarImageBox";
import {
  inputClass,
  parseDobToInputFormat,
  validateDetails,
} from "../utils/aadhaarHelpers";

// FIRST: user uploads any side | SECOND: we ask for the missing side | MANUAL: fallback
const STEP = { FIRST: "first", SECOND: "second", MANUAL: "manual" };

const SIDE_TEXT = {
  front: {
    title: "Ab apne Aadhaar Card ki Front Side ki photo upload Karein",
    hint: "upload Front Side of aadhar card",
    wrong:
      "Aapne Back Side dobara upload ki hai. Kripya Aadhaar ki Front Side ki photo upload karein.",
  },
  back: {
    title: "Ab apne Aadhaar Card ki Back Side ki photo upload Karein",
    hint: "upload Back Side of aadhar card",
    wrong:
      "Aapne Front Side dobara upload ki hai. Kripya Aadhaar ki Back Side ki photo upload karein.",
  },
};

const DobAddressModal = ({ onClose, onSuccess, isEditing = false }) => {
  const { uploadSide, saveDetails, isUploading, isSaving } =
    useAadhaarUpload(isEditing);

  const [step, setStep] = useState(STEP.FIRST);
  const [firstFile, setFirstFile] = useState(null);
  const [secondFile, setSecondFile] = useState(null);
  const [needSide, setNeedSide] = useState(null); // side still missing after 1st upload
  const [sideError, setSideError] = useState("");
  const [dob, setDob] = useState("");
  const [address, setAddress] = useState("");
  const [errors, setErrors] = useState({});

  const isBusy = isUploading || isSaving;
  const canContinue =
    (step === STEP.FIRST && !!firstFile) ||
    (step === STEP.SECOND && !!secondFile) ||
    step === STEP.MANUAL;

  const handleFirstChange = (file) => {
    setSideError("");
    setFirstFile(file);
  };
  const handleSecondChange = (file) => {
    setSideError("");
    setSecondFile(file);
  };

  // Validate + save; fall back to manual entry only if dob/address are missing or unusable
  const finish = async (rawDob, rawAddress) => {
    // Normalised only for validation / the manual date input.
    // The backend gets the dob exactly as the Aadhaar API returned it.
    const finalDob = parseDobToInputFormat(rawDob);
    const finalAddress = rawAddress || "";
    const validation = validateDetails({
      dob: finalDob,
      address: finalAddress,
    });

    if (Object.keys(validation).length > 0) {
      setDob(finalDob);
      setAddress(finalAddress);
      setErrors(step === STEP.MANUAL ? validation : {});
      setStep(STEP.MANUAL);
      return;
    }

    try {
      await saveDetails({ dob: rawDob, address: finalAddress });
      toast.success("Details updated successfully!");
      onSuccess();
    } catch (err) {
      console.error("Error updating details:", err);
      toast.error("Failed to update details");
    }
  };

  const handleContinue = async () => {
    try {
      if (step === STEP.FIRST) {
        const data = await uploadSide(firstFile, "front");

        // nothing readable → stay here, ask for a clearer photo
        if (!data.dob && !data.address) {
          setSideError(
            "Aadhaar padha nahi ja saka. Kripya saaf photo upload karein."
          );
          setFirstFile(null);
          return;
        }
        // single image had both
        if (data.dob && data.address) return finish(data.dob, data.address);

        // only one side read → ask for the other one
        setDob(data.dob || "");
        setAddress(data.address || "");
        setNeedSide(data.dob ? "back" : "front");
        setStep(STEP.SECOND);
        return;
      }

      if (step === STEP.SECOND) {
        const data = await uploadSide(secondFile, needSide);
        const gotNeeded = needSide === "back" ? !!data.address : !!data.dob;

        // same side uploaded again → don't proceed, don't fallback
        if (!gotNeeded) {
          setSideError(SIDE_TEXT[needSide].wrong);
          setSecondFile(null);
          return;
        }
        return finish(dob || data.dob, address || data.address);
      }

      await finish(dob, address); // manual
    } catch (err) {
      console.error("Aadhaar step failed:", err);
      toast.error(err.message || "Something went wrong. Please try again.");
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black bg-opacity-40 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-h-[80vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 flex-shrink-0">
          <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
            <span className="w-8 h-8 bg-white border border-indigo-200 rounded-full flex items-center justify-center shadow-sm text-base">
              🔐
            </span>
            {isEditing ? "Update Your Aadhaar" : "Verify Your Aadhaar"}
          </h3>
          <button
            onClick={() => onClose()}
            className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full p-2 transition"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">
          {step !== STEP.MANUAL && (
            <AadhaarImageBox
              title="Apne Aadhaar Card ki photo upload Karein"
              hint="upload any side of aadhar card"
              file={firstFile}
              onChange={handleFirstChange}
              disabled={step !== STEP.FIRST || isBusy}
              externalError={step === STEP.FIRST ? sideError : ""}
            />
          )}

          {step === STEP.SECOND && (
            <AadhaarImageBox
              title={SIDE_TEXT[needSide].title}
              hint={SIDE_TEXT[needSide].hint}
              file={secondFile}
              onChange={handleSecondChange}
              disabled={isBusy}
              externalError={sideError}
            />
          )}

          {step === STEP.MANUAL && (
            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  ⚠️ Hum aapke Aadhaar se sabhi details nahi padh paaye. Kripya
                  DOB aur address khud bhariye.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date of Birth <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={dob}
                  onChange={(e) => {
                    setDob(e.target.value);
                    setErrors((p) => ({ ...p, dob: "" }));
                  }}
                  className={inputClass(errors.dob)}
                />
                {errors.dob && (
                  <p className="text-red-600 text-sm mt-1">⚠️ {errors.dob}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Complete Address <span className="text-red-500">*</span>
                </label>
                <textarea
                  rows={3}
                  value={address}
                  onChange={(e) => {
                    setAddress(e.target.value);
                    setErrors((p) => ({ ...p, address: "" }));
                  }}
                  placeholder="Enter your full address"
                  className={`${inputClass(errors.address)} resize-none`}
                />
                {errors.address && (
                  <p className="text-red-600 text-sm mt-1">
                    ⚠️ {errors.address}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="px-4 pb-4 pt-3 border-t border-gray-100 flex-shrink-0">
          <p className="text-center text-xs text-gray-500 mb-3">
            Nishchint rahiye, aapka Aadhaar sirf background verification ke liye
            istemal hoga aur aapki jankari surakshit rahegi.
          </p>
          <button
            onClick={handleContinue}
            disabled={!canContinue || isBusy}
            className={`w-full py-3 rounded-full font-semibold flex items-center justify-center gap-2 transition-all ${
              canContinue && !isBusy
                ? "bg-indigo-600 text-white hover:bg-indigo-700 shadow-md"
                : "bg-[#cccccc] text-gray-600 cursor-not-allowed"
            }`}
          >
            {isBusy ? (
              <>
                <Loader className="h-5 w-5 animate-spin" />
                {isUploading ? "Reading Aadhaar..." : "Saving..."}
              </>
            ) : (
              <span>Aage Badein</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DobAddressModal;